// ============================================================================
// ImagePreview — просмотр изображения по id файла (SSoT для предпросмотра).
//
// Онлайн — прямой стриминг `/files/{id}?token=…&base=…` (Range, без чтения
// всего файла в память и без base64). Офлайн — blob из кэша чтения
// (ensureFileBlob). Если стриминг не отдался (права/сеть) — честный переход
// на кэш, затем явная ошибка с кнопкой «Скачать».
//
// Имя файла видно ВСЕГДА (подпись под картинкой), поэтому предпросмотр не
// выглядит «пустым окном» даже на медленной сети.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, ImageOff } from 'lucide-react';
import { fileStreamUrl } from '@/components/media/MediaPlayer';
import { ensureFileBlob } from '@/lib/fileSync';
import { fileBase64 } from '@/lib/fileBytes';
import { downloadBytes } from '@/lib/printForm';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';

export interface ImagePreviewProps {
  fileId: string;
  fileName: string;
}

export function ImagePreview({ fileId, fileName }: ImagePreviewProps) {
  const token = useAuthStore((s) => s.token) || '';
  const baseSlug = useCenterStore((s) => s.baseSlug);
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  const streamUrl = useMemo(
    () => (online && token ? fileStreamUrl(fileId, token, baseSlug) : ''),
    [online, token, fileId, baseSlug],
  );

  const [src, setSrc] = useState(streamUrl);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const objectUrlRef = useRef('');
  const triedCacheRef = useRef(false);

  const releaseObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
  };

  // Офлайн (или после ошибки стриминга) — blob из кэша чтения/SDK.
  const loadFromCache = useCallback(async () => {
    if (triedCacheRef.current) return;
    triedCacheRef.current = true;
    setBusy(true);
    try {
      const { blob } = await ensureFileBlob(fileId);
      const url = URL.createObjectURL(blob);
      releaseObjectUrl();
      objectUrlRef.current = url;
      setSrc(url);
      setError('');
    } catch (e: unknown) {
      logErrorToJournal(e, 'Предпросмотр изображения: загрузка из кэша');
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }, [fileId]);

  useEffect(() => {
    setSrc(streamUrl);
    setError('');
    triedCacheRef.current = false;
    releaseObjectUrl();
    if (!streamUrl) void loadFromCache();
    return releaseObjectUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, streamUrl]);

  const onImageError = () => {
    // Стриминг не отдался — пробуем кэш/скачивание через SDK (один раз).
    if (src && src !== objectUrlRef.current) {
      void loadFromCache();
      return;
    }
    setError('Не удалось показать изображение');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await getSDK().files.downloadFile({ id: fileId });
      const b64 = fileBase64(r?.data);
      if (!b64) throw new Error('файл пуст');
      downloadBytes(fileName || 'image', 'application/octet-stream', b64);
    } catch (e: unknown) {
      logErrorToJournal(e, 'Предпросмотр изображения: скачивание');
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  const caption = (
    <div className="mt-2 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
      <span className="min-w-0 flex-1 truncate" title={fileName}>{fileName}</span>
      <button
        type="button"
        className="inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] text-foreground hover:bg-muted/50 disabled:opacity-50"
        onClick={() => void save()}
        disabled={saving}
      >
        <Download className="h-3 w-3" /> {saving ? 'Скачиваем…' : 'Скачать'}
      </button>
    </div>
  );

  if (error) {
    return (
      <div className="space-y-2">
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border bg-muted/20 px-3 py-8 text-center">
          <ImageOff className="h-6 w-6 text-muted-foreground" />
          <div className="text-xs text-destructive">{error}</div>
        </div>
        {caption}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {src ? (
        <img
          src={src}
          alt={fileName}
          draggable={false}
          onError={onImageError}
          className="mx-auto block max-h-[68vh] w-auto max-w-full rounded-lg border bg-muted/20 object-contain"
        />
      ) : (
        <div className="flex h-44 items-center justify-center rounded-lg border bg-muted/20 text-xs text-muted-foreground">
          {busy ? 'Загрузка изображения…' : 'Подготовка изображения…'}
        </div>
      )}
      {caption}
    </div>
  );
}
